// Logique PURE de l'assistant « Nouveau modèle » — testable sans rendu React.
//
// Tout ce qui décide (validité d'une étape, matrice couleur × pointure, corps
// envoyé au serveur, brouillon) vit ici. Le composant ne fait qu'afficher et
// collecter.
//
// Le serveur reste seul juge : il revalide tout et refuse un doublon récent en
// 409. Ce fichier sert à ne PAS envoyer ce qui est manifestement incomplet, et
// à dire POURQUOI — pas à décider à sa place.

/*
 * LE BROUILLON APPARTIENT À QUELQU'UN.
 *
 * La clé était FIXE : sur une tablette partagée au comptoir — le cas normal
 * dans la boutique — la saisie inachevée de l'une était proposée à l'autre au
 * démarrage de l'écran. Pas une fuite (les montants ne sont déjà pas réinjectés
 * à qui n'a pas le droit de les saisir), mais un contresens : on propose de
 * « reprendre VOTRE saisie » alors que ce n'est pas la sienne.
 *
 * Même motif que le reste de l'application (`draftKey` de utils/formDraft) :
 * la clé porte l'identifiant.
 */
export const CLE_BROUILLON_LEGACY = 'ndoe:nouveau-produit:brouillon';
export const cleBrouillon = (idUser) => `ndoe:nouveau-produit:brouillon:${idUser || 'anon'}`;

/*
 * LA CLÉ D'UNE SAISIE — pour qu'un double envoi ne crée qu'un modèle.
 *
 * Fabriquée UNE FOIS par saisie et renvoyée à chaque tentative : c'est ce qui
 * permet au serveur de reconnaître « la même création » plutôt que d'en deviner
 * une seconde. Elle est renouvelée après un succès, jamais entre deux essais —
 * sinon un renvoi après un réseau lent porterait une clé neuve et créerait un
 * jumeau, ce qui est exactement le problème à éviter.
 *
 * `crypto.randomUUID` n'existe pas sur les WebView anciennes ni hors HTTPS :
 * le repli n'a pas besoin d'être cryptographique, seulement de ne pas se
 * répéter entre deux saisies.
 */
export function nouvelleCleCreation() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch (_) { /* repli ci-dessous */ }
  const alea = () => Math.random().toString(36).slice(2, 10);
  return `k-${Date.now().toString(36)}-${alea()}-${alea()}`;
}

// ---- Étape 1 : l'identité du modèle -------------------------------------------
export function identiteValide(p = {}) {
  return Boolean(
    String(p.nom_produit || '').trim()
    && p.id_categorie && p.id_marque && p.id_matiere && p.id_cible,
  );
}

export function identiteRaison(p = {}) {
  if (!String(p.nom_produit || '').trim()) return 'Donnez un nom au modèle.';
  if (!p.id_categorie) return 'Choisissez une catégorie.';
  if (!p.id_marque) return 'Choisissez une marque.';
  if (!p.id_matiere) return 'Choisissez une matière.';
  if (!p.id_cible) return 'Choisissez une cible.';
  return null;
}

// ---- Étape 2 : la matrice couleur × pointure ----------------------------------
//
// `matrice` : { [idCouleur]: { [idTaille]: quantite } }. Une cellule à 0 ou vide
// n'existe pas — on ne crée QUE ce qui est réellement reçu. C'est la règle qui
// évite le catalogue rempli de variantes fantômes à stock zéro.
export function lignesDeLaMatrice(matrice = {}) {
  const out = [];
  Object.entries(matrice).forEach(([idCouleur, parTaille]) => {
    Object.entries(parTaille || {}).forEach(([idTaille, q]) => {
      const quantite = Number(q);
      if (Number.isInteger(quantite) && quantite > 0) {
        out.push({ id_couleur: Number(idCouleur), id_taille: Number(idTaille), quantite });
      }
    });
  });
  return out;
}

export function totalPaires(matrice = {}) {
  return lignesDeLaMatrice(matrice).reduce((s, l) => s + l.quantite, 0);
}

export function couleursUtilisees(matrice = {}) {
  return [...new Set(lignesDeLaMatrice(matrice).map((l) => l.id_couleur))];
}

/*
 * LA MARCHANDISE EST FACULTATIVE (10/08/2026, demande de la propriétaire :
 * « nous devons garder la possibilité de créer une fiche sans marchandise »).
 *
 * C'est la fiche préparée AVANT l'arrivée d'un conteneur : on connaît le modèle
 * et son prix, pas encore ce qui arrivera. L'ancien formulaire le permettait ;
 * l'assistant l'exigeait, ce qui obligeait à garder l'ancien écran vivant pour
 * ce seul usage.
 *
 * On ne valide donc plus « au moins une quantité » : on valide la COHÉRENCE.
 * Une saisie incohérente est une couleur choisie sans aucune quantité en face —
 * là, l'utilisatrice a commencé quelque chose qu'elle n'a pas fini.
 */
export function matriceValide(matrice = {}, couleursChoisies = []) {
  // Aucune couleur : fiche sans marchandise, parfaitement valide.
  if (!couleursChoisies.length) return true;
  // Des couleurs choisies : il faut au moins une quantité, sinon c'est une
  // saisie en cours d'abandon.
  return lignesDeLaMatrice(matrice).length > 0;
}

/*
 * FAUT-IL DEMANDER CONFIRMATION AVANT DE CRÉER LA FICHE ?
 *
 * Incident du 17/08/2026. Après une réception, « un article est apparu sans
 * pointure et sans photo » : le produit 658, créé sans aucune couleur — donc
 * sans variante, et pointure comme photo vivent sur la variante.
 *
 * La fiche sans marchandise est une capacité VOULUE (demande du 10/08). Mais
 * en assouplissant la validation pour l'accepter, on a rendu ce cas
 * indiscernable d'une grille non remplie. La règle ne change pas — on exige
 * désormais un OUI EXPLICITE, pour que le voulu ne se confonde plus avec
 * l'oublié.
 */
export function confirmationSansMarchandiseRequise(couleursChoisies = []) {
  return !((couleursChoisies || []).length);
}

export function matriceRaison(matrice = {}, couleursChoisies = []) {
  if (!couleursChoisies.length) return null;   // fiche sans marchandise
  if (!matriceValide(matrice, couleursChoisies)) {
    return 'Vous avez choisi des couleurs : saisissez au moins une quantité, ou retirez-les pour créer une fiche sans marchandise.';
  }
  return null;
}

/*
 * LA DATE D'ENTRÉE (10/08/2026, demande de la propriétaire : « mettre une date
 * passée »).
 *
 * Elle sert quand on enregistre aujourd'hui un arrivage arrivé le mois dernier :
 * les rapports d'ancienneté de stock et de rotation s'appuient dessus. Sans
 * date, le serveur applique le jour de la saisie — le cas le plus courant.
 *
 * Les mêmes bornes que le serveur, pour que le refus arrive AVANT l'envoi :
 * ni dans le futur, ni au-delà de cinq ans (une faute de frappe sur l'année
 * fausserait tous les rapports d'ancienneté sans qu'on le voie).
 */
export function dateEntreeRaison(valeur) {
  if (!valeur) return null;                    // facultative
  const d = new Date(valeur);
  if (Number.isNaN(d.getTime())) return "La date d'entrée est illisible.";
  const maintenant = new Date();
  if (d.getTime() > maintenant.getTime() + 86400000) {
    return "La date d'entrée ne peut pas être dans le futur.";
  }
  const cinqAns = new Date(maintenant.getFullYear() - 5, maintenant.getMonth(), maintenant.getDate());
  if (d.getTime() < cinqAns.getTime()) {
    return "Cette date remonte à plus de cinq ans — vérifiez l'année.";
  }
  return null;
}

// ---- Étape 3 : le prix ---------------------------------------------------------
export function prixValide(prix) {
  const p = Number(prix);
  return Number.isInteger(p) && p > 0;
}

export function prixRaison(prix) {
  if (prix === '' || prix === null || prix === undefined) return 'Indiquez le prix de vente.';
  const p = Number(prix);
  if (!Number.isFinite(p) || p <= 0) return 'Le prix de vente doit être positif.';
  if (!Number.isInteger(p)) return 'Le prix se saisit en dollars entiers.';
  return null;
}

// ---- Le corps envoyé au serveur ------------------------------------------------
//
// Regroupé PAR COULEUR, parce que c'est ainsi que le serveur écrit les lots :
// une couleur = un `code_variant` hérité, une image, un ensemble de pointures.
/*
 * LE PAYLOAD SE CONSTRUIT DEPUIS CE QUE L'ON VOIT (17/08/2026).
 *
 * DÉFAUT CORRIGÉ, de gravité maximale. Les coloris étaient dérivés de la seule
 * MATRICE. Or retirer une couleur du sélecteur ne nettoyait pas ses quantités :
 *
 *   1. choisir Noir, saisir P41 = 2 ;
 *   2. retirer Noir de la liste des couleurs ;
 *   3. l'écran affiche « Créer la fiche seule (sans marchandise) », et la
 *      confirmation fait jurer qu'il n'y a AUCUNE paire ;
 *   4. le formulaire envoie pourtant 2 paires de Noir.
 *
 * On créait donc du stock pour une couleur que personne ne voyait plus, en
 * ayant fait confirmer le contraire. `couleursChoisies` fait désormais foi :
 * ce qui n'est pas sélectionné n'existe pas, même si la matrice en garde la
 * trace. Le nettoyage de la matrice à la désélection reste fait par l'écran —
 * mais on ne s'y FIE PAS : la vérité du payload se calcule ici.
 *
 * `couleursChoisies` non fourni = aucun filtre (compatibilité : d'autres
 * appelants passent la seule matrice).
 */
export function construireCorps({ produit = {}, matrice = {}, images = {}, pays = null, arrivage = {}, couleursChoisies = null, cleCreation = null } = {}) {
  const retenues = Array.isArray(couleursChoisies) ? new Set(couleursChoisies.map(Number)) : null;
  const parCouleur = new Map();
  lignesDeLaMatrice(matrice)
    .filter((l) => !retenues || retenues.has(Number(l.id_couleur)))
    .forEach((l) => {
      if (!parCouleur.has(l.id_couleur)) {
        parCouleur.set(l.id_couleur, {
          id_couleur: l.id_couleur,
          id_pays: pays || null,
          // Une photo par COLORIS, jamais une par pointure : c'est la même paire.
          img: images[l.id_couleur] || null,
          lignes: [],
        });
      }
      parCouleur.get(l.id_couleur).lignes.push({ id_taille: l.id_taille, quantite: l.quantite });
    });

  return {
    nom_produit: String(produit.nom_produit || '').trim(),
    id_categorie: produit.id_categorie,
    id_marque: produit.id_marque,
    id_matiere: produit.id_matiere,
    id_cible: produit.id_cible,
    id_hauteur_talon: produit.id_hauteur_talon || undefined,
    // Facultative : sans elle, le serveur date au jour de la saisie.
    date_entrant: produit.date_entrant || undefined,
    prix: Number(produit.prix),
    coloris: [...parCouleur.values()],
    reference: arrivage.reference || undefined,
    commentaire: arrivage.commentaire || undefined,
    montant_marchandise: arrivage.montant_marchandise || undefined,
    montant_frais: arrivage.montant_frais || undefined,
    // La clé de la saisie : le serveur s'en sert pour reconnaître un renvoi.
    // Absente, le serveur retombe sur sa garde des 10 minutes — c'est ce qui
    // laisse les APK déjà installées fonctionner sans changement.
    cle_creation: cleCreation || undefined,
  };
}

// ---- Récapitulatif avant envoi -------------------------------------------------
export function recapitulatif({ matrice = {}, produit = {}, arrivage = {} } = {}) {
  const paires = totalPaires(matrice);
  const prix = Number(produit.prix) || 0;
  const marchandise = Number(arrivage.montant_marchandise) || 0;
  const frais = Number(arrivage.montant_frais) || 0;
  const coutTotal = marchandise + frais;
  const valeurVente = paires * prix;
  return {
    paires,
    couleurs: couleursUtilisees(matrice).length,
    variantes: lignesDeLaMatrice(matrice).length,
    valeurVente,
    coutTotal,
    // Marge PRÉVISIONNELLE : elle n'a de sens que si un coût a été saisi. Sans
    // coût, on ne l'invente pas — c'est la règle du module coût (jamais de zéro
    // présenté comme une vérité).
    marge: coutTotal > 0 ? valeurVente - coutTotal : null,
    tauxMarge: coutTotal > 0 && valeurVente > 0
      ? Math.round(((valeurVente - coutTotal) / valeurVente) * 1000) / 10
      : null,
  };
}

// ---- Brouillon -----------------------------------------------------------------
//
// Un arrivage se saisit en plusieurs minutes, souvent debout dans le dépôt, sur
// un téléphone. Fermer l'onglet par erreur ne doit pas coûter dix minutes de
// ressaisie. Les images ne sont PAS conservées : ce sont des fichiers.
export function brouillonDepuis({ produit, matrice, couleursChoisies, arrivage, etape }) {
  return JSON.stringify({ produit, matrice, couleursChoisies, arrivage, etape, le: Date.now() });
}

export function lireBrouillon(brut, ageMaxMs = 24 * 3600 * 1000) {
  if (!brut) return null;
  try {
    const d = JSON.parse(brut);
    if (!d || typeof d !== 'object') return null;
    // Un brouillon d'hier n'est plus le bon contexte : on ne le propose pas.
    if (d.le && Date.now() - d.le > ageMaxMs) return null;
    return d;
  } catch (e) {
    return null;
  }
}

// Le brouillon mérite-t-il d'être proposé ? Un formulaire à peine effleuré, non.
export function brouillonUtile(d) {
  if (!d) return false;
  return Boolean(String(d.produit?.nom_produit || '').trim()) || totalPaires(d.matrice) > 0;
}

/*
 * RANGER LES POINTURES — celles qui servent devant, les autres derrière.
 *
 * 48 pointures cohabitent sur 4 systèmes, et 19 PORTENT LE MÊME LIBELLÉ :
 * « P35 » existe en européen ET en CLA, sans rien pour les distinguer. La
 * boutique travaille en européen (1562 variantes sur 1563) et 35 pointures
 * n'ont jamais servi. Une liste de 48 entrées dont les trois quarts sont du
 * bruit se parcourt au lieu de s'utiliser — et une paire porte déjà une
 * pointure américaine par erreur.
 *
 * Le système mis en avant est celui qui PORTE LE PLUS DE VARIANTES, pas une
 * liste écrite en dur : le jour où la boutique vend en anglais, l'écran suit
 * tout seul. On affiche TOUTE sa gamme, y compris ses pointures encore
 * inutilisées — une 47 doit être saisissable la première fois.
 *
 * REPLI : si le serveur ne renvoie pas l'usage (API plus ancienne), on rend
 * tout en bloc plutôt que rien.
 */
export function grouperPointures(tailles) {
  const parNombre = (a, b) => Number(a.taille) - Number(b.taille);
  const toutes = (Array.isArray(tailles) ? tailles : []).slice();
  if (!toutes.length) return { courantes: [], autres: [] };

  const servent = toutes.filter((t) => Number(t.variantes) > 0);
  if (!servent.length) return { courantes: toutes.sort(parNombre), autres: [] };

  const poids = servent.reduce((acc, t) => {
    acc[t.id_pays] = (acc[t.id_pays] || 0) + Number(t.variantes);
    return acc;
  }, {});
  const idPays = Object.keys(poids).sort((a, b) => poids[b] - poids[a])[0];

  return {
    courantes: toutes.filter((t) => String(t.id_pays) === String(idPays)).sort(parNombre),
    autres: toutes.filter((t) => String(t.id_pays) !== String(idPays)).sort(parNombre),
  };
}

/*
 * DEUX SYSTÈMES DE POINTURE DANS LE MÊME ARRIVAGE — avertir, pas bloquer.
 *
 * Chaque pointure porte son pays (`id_pays`) : le 38 européen et le 38
 * américain sont deux tailles distinctes. Les mélanger sur un même modèle donne
 * un stock qu'on ne sait plus lire — « P38 » ne veut alors plus rien dire.
 *
 * Mesuré en production le 17/08/2026 : AUCUN cas existant. C'est donc un risque,
 * pas un problème constaté — d'où l'avertissement plutôt que le refus (décision
 * de la propriétaire). Un arrivage peut réellement arriver avec deux marquages.
 *
 * L'écran met en avant le système dominant et replie les autres sous « Autres
 * systèmes », mais rien n'empêche d'y piocher.
 *
 * @returns {string[]} les systèmes concernés, vide s'il n'y a pas de mélange.
 */
export function systemesPointureMelanges(taillesChoisies = [], tailles = []) {
  const parId = new Map((Array.isArray(tailles) ? tailles : []).map((t) => [String(t.id_taille), t]));
  const vus = new Map();
  (Array.isArray(taillesChoisies) ? taillesChoisies : []).forEach((id) => {
    const t = parId.get(String(id));
    if (!t || t.id_pays == null) return;
    // Le libellé lisible d'abord : « EUR » parle, « 3 » non.
    vus.set(String(t.id_pays), t.code_pays || t.nom_pays || `système ${t.id_pays}`);
  });
  return vus.size > 1 ? [...vus.values()] : [];
}
